// PHASE_1 / B3 — the headcount Content component (ADR-014 §2).
// Faz 8.6 / Batch A5 restyles the VISUAL layer to the novem headcount language
// (NovemHR-ekranlar org-extra.jsx OrgNormPanel "Birim bazında doluluk" — serif
// PageHeader+em, a StatCard strip, per-department MeterBar fill rows). The DATA
// layer is untouched: the SAME bounded GET /v1/org/headcount aggregate (no
// pagination). Counts are integers, never money.
//
// The mockup's norm-kadro analysis (norm vs dolu, açık kadro, salary bands,
// hiring priority) has NO backend — the org API carries the active count per
// department only. So the meter fills each department against the ACTIVE TOTAL
// (its share of the tenant), not against a norm target, and the norm/vacancy/
// band columns are skipped, not faked (port rule 8 — recorded in notes).

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Building2, Layers, Users } from "lucide-react";

import {
  Card,
  ErrorState,
  HelpHint,
  Loading,
  MeterBar,
  StatCard,
} from "@novemai/components";

import { errorMessage } from "@/api/errors";
import { DepartmentLink } from "@/components/department-link";
import { fetchHeadcount, type HeadcountResponse } from "./api";

// The short-id rendering convention A1/A2 established for raw UUIDs. It is now
// only a FALLBACK: GET /v1/org/headcount joins the department name server-side,
// so a resolvable department reads as prose and a bare id means the join failed.
const shortId = (id: string): string => id.slice(0, 8);

export function OrgHeadcountContent(): React.ReactElement {
  const { t, i18n } = useTranslation();

  const query = useQuery<HeadcountResponse>({
    queryKey: ["org", "headcount"],
    queryFn: fetchHeadcount,
  });

  const total = query.data?.total_active ?? 0;

  // Department rows sorted by count desc (the mockup's fill list reads
  // largest-first). Pure derivation over the bounded aggregate.
  const rows = useMemo(() => {
    const list = [...(query.data?.by_department ?? [])];
    list.sort((a, b) => b.count - a.count);
    return list;
  }, [query.data]);

  const fmtPct = (n: number): string =>
    new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 }).format(n);

  const largest = rows[0]?.count ?? 0;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {query.isLoading ? (
        <Loading label={t("org.loading")} rows={4} />
      ) : query.isError ? (
        <ErrorState
          title={t("org.error_title")}
          description={errorMessage(query.error, t)}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatCard
              label={t("org.headcount_kpi_total")}
              value={String(total)}
              tone="accent"
              icon={<Users size={18} />}
            />
            <StatCard
              label={t("org.headcount_kpi_departments")}
              value={String(rows.length)}
              tone="info"
              icon={<Building2 size={18} />}
            />
            <StatCard
              // The label promises a unit NAME while the value is a bare count,
              // and the department it belongs to only appears (implicitly) as
              // the first row of the list below.
              label={
                <span className="inline-flex items-center gap-1">
                  {t("org.headcount_kpi_largest")}
                  <HelpHint
                    label={t("common.help_for", { field: t("org.headcount_kpi_largest") })}
                  >
                    {t("org.headcount_kpi_largest_help")}
                  </HelpHint>
                </span>
              }
              value={String(largest)}
              tone="success"
              icon={<Layers size={18} />}
            />
          </div>

          <Card className="overflow-hidden">
            <div className="border-b border-[var(--border-subtle)] px-5 py-4">
              <div className="font-serif text-[18px] text-[var(--fg-primary)]">
                {t("org.headcount_by_department")}
              </div>
              <div className="mt-0.5 font-serif text-[13px] italic text-[var(--fg-muted)]">
                {t("org.headcount_total_value", { count: total })}
              </div>
            </div>
            <div className="px-5 py-2">
              {rows.map((row, i) => {
                // Name first; the short id only survives as the fallback for a
                // department whose row cannot be resolved (a deleted department
                // still referenced by an employee), so an unreadable UUID is a
                // visible anomaly rather than the normal rendering.
                const label =
                  row.department_name ??
                  (row.department_id
                    ? shortId(row.department_id)
                    : t("org.headcount_unassigned"));
                const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
                return (
                  <div
                    key={row.department_id ?? "__unassigned__"}
                    className={
                      "py-3.5" +
                      (i > 0 ? " border-t border-[var(--border-subtle)]" : "")
                    }
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span
                        className={
                          // Monospace marks a RAW IDENTIFIER, not "has a
                          // department" — a named department reads as prose.
                          "truncate text-[15px] text-[var(--fg-primary)]" +
                          (row.department_name === null && row.department_id !== null
                            ? " font-mono text-[13px]"
                            : " font-serif")
                        }
                      >
                        {/* The unassigned bucket keeps its plain text on its
                            own: it has no department_id, and `?department_id=`
                            cannot express "has none". */}
                        <DepartmentLink departmentId={row.department_id}>
                          {label}
                        </DepartmentLink>
                      </span>
                      <span className="shrink-0 font-sans text-[12.5px] text-[var(--fg-muted)]">
                        {t("org.headcount_share", { count: row.count, percent: fmtPct(pct) })}
                      </span>
                    </div>
                    <MeterBar
                      ratio={total > 0 ? row.count / total : 0}
                      tone="accent"
                      label={t("org.headcount_meter_label", {
                        count: row.count,
                        total,
                      })}
                    />
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
